// Per-route real-user performance telemetry.
//
// `web-v1` is deliberately an app-defined score, not an official Lighthouse
// score. Web Lighthouse retired TTI and FID, so this contract measures TTFF
// (first contentful paint for hard visits; first painted route frame for soft
// visits) plus an explicit route-ready boundary supplied by the route owner.

import type {
  RoutePerfCategory,
  RoutePerfNavigationType,
  RoutePerfRoute,
} from '@/events/performance';

export const ROUTE_PERF_SCORE_VERSION = 'web-v1' as const;

const TTFF_GOOD_MS = 1_000;
const TTFF_POOR_MS = 3_000;
const ROUTE_READY_GOOD_MS = 2_000;
const ROUTE_READY_POOR_MS = 6_000;
const TTFF_WEIGHT = 0.4;
const ROUTE_READY_WEIGHT = 0.6;

export interface RoutePerfScore {
  readonly score: number;
  readonly category: RoutePerfCategory;
}

export interface RoutePerfSample {
  readonly route: RoutePerfRoute;
  readonly navigationType: RoutePerfNavigationType;
  readonly ttffMs: number;
  /** App-defined route readiness, also exported as the compatibility `tti_ms` alias. */
  readonly routeReadyMs: number;
  readonly score: number;
  readonly category: RoutePerfCategory;
  readonly scoreVersion: typeof ROUTE_PERF_SCORE_VERSION;
}

export interface RoutePerfVisitOptions {
  readonly route: RoutePerfRoute;
  readonly navigationType: RoutePerfNavigationType;
  readonly startedAt: number;
  readonly onComplete: (sample: RoutePerfSample) => void;
}

export interface RoutePerfVisit {
  readonly navigationType: RoutePerfNavigationType;
  markPaint(at: number): void;
  markReady(at: number): void;
  isComplete(): boolean;
}

interface PendingSoftNavigation {
  readonly route: RoutePerfRoute;
  readonly pathname: string;
  readonly startedAt: number;
  readonly navigationType: Extract<RoutePerfNavigationType, 'soft' | 'back_forward'>;
  paintedAt?: number;
}

let pendingSoftNavigation: PendingSoftNavigation | null = null;
let claimedSoftNavigation: {
  readonly pathname: string;
  readonly visit: RoutePerfVisit;
} | null = null;

function scoreMetric(value: number, good: number, poor: number): number {
  if (value <= good) return 100;
  if (value >= poor) return 0;
  return Math.round(((poor - value) / (poor - good)) * 100);
}

/**
 * Auditable `web-v1` score: TTFF 40% + app-defined route readiness 60%.
 * Missing inputs intentionally return no result; missing data never improves
 * the score. Raw INP remains an independent page-level Web Vital.
 */
export function scoreRoutePerformance(
  ttffMs: number | undefined,
  routeReadyMs: number | undefined,
): RoutePerfScore | undefined {
  if (ttffMs === undefined || routeReadyMs === undefined) return undefined;
  const ttffScore = scoreMetric(ttffMs, TTFF_GOOD_MS, TTFF_POOR_MS);
  const readyScore = scoreMetric(routeReadyMs, ROUTE_READY_GOOD_MS, ROUTE_READY_POOR_MS);
  const score = Math.round(ttffScore * TTFF_WEIGHT + readyScore * ROUTE_READY_WEIGHT);
  const category: RoutePerfCategory =
    score >= 90 ? 'good' : score >= 50 ? 'needs-improvement' : 'poor';
  return { score, category };
}

/** Create a visit state machine independent of browser APIs for deterministic tests. */
export function createRoutePerfVisit(options: RoutePerfVisitOptions): RoutePerfVisit {
  let ttffMs: number | undefined;
  let routeReadyMs: number | undefined;
  let complete = false;

  function tryComplete(): void {
    if (complete) return;
    const scored = scoreRoutePerformance(ttffMs, routeReadyMs);
    if (scored === undefined) return;
    complete = true;
    options.onComplete({
      route: options.route,
      navigationType: options.navigationType,
      ttffMs: ttffMs as number,
      routeReadyMs: routeReadyMs as number,
      score: scored.score,
      category: scored.category,
      scoreVersion: ROUTE_PERF_SCORE_VERSION,
    });
  }

  return {
    navigationType: options.navigationType,
    markPaint(at: number): void {
      if (complete || ttffMs !== undefined) return;
      ttffMs = Math.max(0, Math.round(at - options.startedAt));
      tryComplete();
    },
    markReady(at: number): void {
      if (complete || routeReadyMs !== undefined) return;
      routeReadyMs = Math.max(0, Math.round(at - options.startedAt));
      tryComplete();
    },
    isComplete(): boolean {
      return complete;
    },
  };
}

function stripLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (/^[a-z]{2}$/i.test(segments[0] ?? '')) segments.shift();
  if (segments[0] === 'demo-mode') segments.shift();
  return `/${segments.join('/')}`;
}

/** Converts browser paths into the bounded route labels accepted by the server. */
export function routeFromPathname(pathname: string): RoutePerfRoute | undefined {
  const path = stripLocale(pathname);
  if (path === '/') return 'markets';
  const segments = path.split('/').filter(Boolean);
  const first = segments[0];
  if (first === 'markets') return segments.length === 1 ? 'markets' : 'market_detail';
  if (first === 'proposals') return segments.length === 1 ? 'proposals' : 'proposal_detail';
  if (first === 'winners') return segments.length === 1 ? 'winners' : 'winner_detail';
  if (first === 'portfolio') return segments.length === 1 ? 'portfolio' : 'profile';
  if (path === '/onboarding') return 'onboarding';
  if (path === '/auth/callback') return 'auth_callback';
  if (path === '/settings') return 'settings';
  return undefined;
}

/** Called by the History API tracker before the App Router renders the next route. */
export function routePerfNavigationStarted(
  pathname: string,
  startedAt: number,
  navigationType: Extract<RoutePerfNavigationType, 'soft' | 'back_forward'> = 'soft',
): void {
  const route = routeFromPathname(pathname);
  claimedSoftNavigation = null;
  pendingSoftNavigation =
    route === undefined ? null : { route, pathname, startedAt, navigationType };
}

/** Called after the first post-navigation frame paints. */
export function routePerfNavigationPainted(pathname: string, paintedAt: number): void {
  const claimed = claimedSoftNavigation;
  if (claimed?.pathname === pathname) {
    claimedSoftNavigation = null;
    claimed.visit.markPaint(paintedAt);
    return;
  }
  const pending = pendingSoftNavigation;
  if (pending === null || pending.pathname !== pathname) return;
  pendingSoftNavigation = { ...pending, paintedAt };
}

function navigationType(): RoutePerfNavigationType {
  if (typeof performance === 'undefined') return 'hard';
  const entry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return entry?.type === 'back_forward' ? 'back_forward' : 'hard';
}

/** Claim the pending soft navigation for a declarative route boundary, or start a hard visit. */
export function beginRoutePerfVisit(
  route: RoutePerfRoute,
  onComplete: (sample: RoutePerfSample) => void,
): RoutePerfVisit {
  const pending = pendingSoftNavigation;
  if (pending?.route === route) {
    pendingSoftNavigation = null;
    const visit = createRoutePerfVisit({
      route,
      navigationType: pending.navigationType,
      startedAt: pending.startedAt,
      onComplete,
    });
    if (pending.paintedAt !== undefined) {
      visit.markPaint(pending.paintedAt);
    } else {
      claimedSoftNavigation = { pathname: pending.pathname, visit };
    }
    return visit;
  }
  return createRoutePerfVisit({
    route,
    navigationType: navigationType(),
    startedAt: 0,
    onComplete,
  });
}
